import { useEffect } from "react";

import { FlexProps } from "@chakra-ui/react";
import { Navigate } from "react-router";

import { PageName } from "analytics/events";
import { TabBarItemID, useLayoutStore } from "app/stores/useLayoutStore";
import { EppoFeatureFlags } from "app/types/flags";
import { WaitingOrderBannerType } from "app/types/waitingOrderBanner";
import { routes as routesConfig } from "config/routes";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";

export type Flag = {
  featureFlag?: EppoFeatureFlags;
  inaccessible?: boolean;
  redirectPath?: string;
};

export type RouteLayoutProps = {
  canGoBack?: boolean;
  withTabBar?: boolean;
  highlightedTab?: TabBarItemID | null;
  withWaitingOrdersBannerOfType?: WaitingOrderBannerType;
  withMenu?: boolean;
  withOfflineBanner?: boolean;
  withAppHeader?: boolean;
  withScrollableAppHeader?: boolean;
  withOngoingActivityBanner?: boolean;
  withTabList?: boolean;
  canShowUpdateModal?: boolean;
  pageName?: PageName | null;
  flag?: Flag;
};

export type AppLayoutProviderProps = RouteLayoutProps & FlexProps & { id: string };

function ProtectedRoute({
  featureFlag,
  redirectPath = routesConfig.activities.root,
  inaccessible = false,
  children,
}: {
  featureFlag: EppoFeatureFlags;
  redirectPath?: string;
  inaccessible?: boolean;
  children: React.ReactNode;
}) {
  const { isFeatureEnabled, isLoading } = useEppoFeatureFlagProvider(featureFlag);

  // Only block rendering on the INITIAL load, before we have any decision.
  // Background refetches (e.g. flags re-fetched on tab focus / returning from
  // the camera on a handheld) keep isLoading=true but retain the prior
  // decision — returning null here would unmount the page and wipe all
  // in-progress form state underneath the user.
  if (isLoading && isFeatureEnabled === null) return null;

  const shouldRedirect = inaccessible ? isFeatureEnabled : !isFeatureEnabled;

  if (shouldRedirect) {
    return <Navigate to={redirectPath} replace />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

export function AppLayoutProvider({
  children,
  id,
  canGoBack = true,
  withTabBar = false,
  highlightedTab = null,
  withWaitingOrdersBannerOfType = "default",
  withMenu = true,
  withOfflineBanner = true,
  withAppHeader = true,
  withScrollableAppHeader = false,
  withOngoingActivityBanner = false,
  withTabList = false,
  canShowUpdateModal = false,
  pageName = null,
  flag,
}: AppLayoutProviderProps) {
  const setAppLayout = useLayoutStore((state) => state.setAppLayout);

  useEffect(() => {
    setAppLayout({
      canGoBack,
      withTabBar,
      highlightedTab,
      withWaitingOrdersBannerOfType,
      withMenu,
      withOfflineBanner,
      withAppHeader,
      withScrollableAppHeader,
      withOngoingActivityBanner,
      withTabList,
      canShowUpdateModal,
      pageName,
    });
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (flag?.featureFlag) {
    return (
      <ProtectedRoute
        featureFlag={flag.featureFlag}
        inaccessible={flag.inaccessible}
        redirectPath={flag.redirectPath}
      >
        {children}
      </ProtectedRoute>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}
